import React, { useState, useEffect, useCallback, useRef } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { fetchFinancePaymentReport } from '../../services/incentiveTicketsService';
import { formatOMR } from '../../utils/formatCurrency';
import { useAuth } from '../../contexts/AuthContext';

const COMPANY_NAME = 'Electronic City for IT Services';
const COMPANY_SUBTITLE = 'Project Incentive Management System';

const FinancePaymentReport = () => {
  const { user } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    projectName: '',
    clientName: '',
    employee: '',
    department: '',
    status: '',
    paymentStatus: '',
  });
  const [reportMeta, setReportMeta] = useState({
    reportNo: `FPR-${new Date()?.getFullYear()}-${String(new Date()?.getMonth() + 1)?.padStart(2, '0')}`,
    batchNo: '',
    preparedBy: '',
    reviewedBy: '',
    approvedBy: '',
    notes: '',
  });
  const printRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchFinancePaymentReport(filters);
      setReportData(data);
    } catch (err) {
      setError(err?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const handleMetaChange = (key, value) => setReportMeta(prev => ({ ...prev, [key]: value }));

  const handlePrint = () => window.print();

  const handleExportCSV = () => {
    const today = new Date()?.toISOString()?.split('T')?.[0]?.replace(/-/g, '_');
    const header = ['No.', 'Ticket #', 'Project Name', 'Client Name', 'Employee Name', 'Department', 'Role', 'Incentive Type', 'Incentive Amount (OMR)', 'Payment Status', 'Payment Reference', 'Payment Date'];
    const rows = (reportData?.rows || [])?.map((r, i) => [
      i + 1, r?.ticketNumber, r?.projectName, r?.clientName, r?.employeeName,
      r?.department, r?.role, r?.memberType,
      (parseFloat(r?.incentiveAmount) || 0)?.toFixed(3),
      r?.paymentStatus, r?.paymentReference, r?.paymentDate
    ]);
    const csv = [header, ...rows]?.map(r => r?.map(c => `"${c}"`)?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Finance_Payment_Report_${today}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const generatedDate = new Date()?.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const generatedBy = user?.profile?.full_name || user?.email || 'System';

  const summaryCards = [
    { label: 'Total Sales Incentives', value: formatOMR(reportData?.summary?.totalSalesIncentives || 0), icon: 'TrendingUp', color: 'from-blue-500 to-blue-600' },
    { label: 'Total Project Team Incentives', value: formatOMR(reportData?.summary?.totalProjectIncentives || 0), icon: 'Users', color: 'from-purple-500 to-purple-600' },
    { label: 'Grand Total Payable', value: formatOMR(reportData?.summary?.grandTotal || 0), icon: 'Banknote', color: 'from-success to-emerald-600' },
    { label: 'Paid Amount', value: formatOMR(reportData?.summary?.paidAmount || 0), icon: 'CheckCircle', color: 'from-emerald-500 to-emerald-700' },
    { label: 'Pending Amount', value: formatOMR(reportData?.summary?.pendingAmount || 0), icon: 'Clock', color: 'from-warning to-orange-500' },
    { label: 'No. of Employees', value: reportData?.summary?.numberOfEmployees || 0, icon: 'UserCheck', color: 'from-primary to-accent', isNumber: true },
    { label: 'No. of Tickets', value: reportData?.summary?.numberOfTickets || 0, icon: 'Ticket', color: 'from-orange-500 to-orange-600', isNumber: true },
  ];

  return (
    <>
      {/* ===== PRINT STYLES ===== */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
          body * { visibility: hidden !important; }
          #finance-print-area, #finance-print-area * { visibility: visible !important; }
          #finance-print-area { position: fixed !important; top: 0 !important; left: 0 !important; width: 100% !important; background: white !important; color: black !important; font-family: Arial, sans-serif !important; font-size: 10pt !important; }
          .no-print { display: none !important; }
          .print-table { width: 100%; border-collapse: collapse; }
          .print-table th { background: #1a3a5c !important; color: white !important; padding: 6px 8px; text-align: left; font-size: 9pt; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-table td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; font-size: 9pt; }
          .print-table tr:nth-child(even) td { background: #f9fafb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-table thead { display: table-header-group; }
          .print-table tfoot { display: table-footer-group; }
          .print-summary-box { display: inline-block; border: 1px solid #d1d5db; padding: 8px 12px; margin: 4px; border-radius: 6px; }
          .print-header-logo { font-size: 16pt; font-weight: bold; color: #1a3a5c; }
          .print-section-title { font-size: 12pt; font-weight: bold; color: #1a3a5c; margin: 8px 0 4px; }
          .page-break-avoid { page-break-inside: avoid; }
          .signature-line { border-top: 1px solid #374151; padding-top: 4px; margin-top: 40px; font-size: 9pt; }
        }
      `}</style>
      <MainLayout>
        <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in">
          {/* Screen Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 no-print">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Finance Payment Report
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Tickets with status Approved, Ready for Payment, or Paid</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" iconName="Printer" iconPosition="left" onClick={handlePrint}>Print</Button>
              <Button variant="outline" iconName="Download" iconPosition="left" onClick={handleExportCSV}>Export CSV</Button>
            </div>
          </div>

          {/* Report Meta Fields */}
          <div className="bg-card border border-border rounded-2xl p-4 shadow-sm no-print">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Report Information</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { key: 'reportNo', label: 'Report No' },
                { key: 'batchNo', label: 'Payment Batch No' },
                { key: 'preparedBy', label: 'Prepared By' },
                { key: 'reviewedBy', label: 'Reviewed By (Finance)' },
                { key: 'approvedBy', label: 'Approved By' },
              ]?.map(f => (
                <div key={f?.key}>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">{f?.label}</label>
                  <input
                    type="text"
                    value={reportMeta?.[f?.key]}
                    onChange={e => handleMetaChange(f?.key, e?.target?.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              ))}
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Notes</label>
                <input
                  type="text"
                  value={reportMeta?.notes}
                  onChange={e => handleMetaChange('notes', e?.target?.value)}
                  placeholder="Optional notes..."
                  className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-2xl p-4 shadow-sm no-print">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'dateFrom', label: 'Period From', type: 'date' },
                { key: 'dateTo', label: 'Period To', type: 'date' },
                { key: 'projectName', label: 'Project', type: 'text', placeholder: 'Filter by project...' },
                { key: 'employee', label: 'Employee', type: 'text', placeholder: 'Filter by employee...' },
                { key: 'department', label: 'Department', type: 'text', placeholder: 'Filter by department...' },
              ]?.map(f => (
                <div key={f?.key}>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">{f?.label}</label>
                  <input
                    type={f?.type}
                    value={filters?.[f?.key]}
                    onChange={e => handleFilterChange(f?.key, e?.target?.value)}
                    placeholder={f?.placeholder}
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              ))}
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Ticket Status</label>
                <select value={filters?.status} onChange={e => handleFilterChange('status', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50">
                  <option value="">All</option>
                  <option value="Approved">Approved</option>
                  <option value="Ready for Payment">Ready for Payment</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Payment Status</label>
                <select value={filters?.paymentStatus} onChange={e => handleFilterChange('paymentStatus', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50">
                  <option value="">All</option>
                  <option value="Pending">Pending</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={load} className="w-full">Refresh</Button>
              </div>
            </div>
          </div>

          {/* Summary Cards (screen) */}
          {!loading && reportData && (
            <div className="grid grid-cols-2 md:grid-cols-7 gap-3 no-print">
              {summaryCards?.map(card => (
                <div key={card?.label} className="bg-card border border-border rounded-2xl p-3 shadow-sm">
                  <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${card?.color} flex items-center justify-center mb-2`}>
                    <Icon name={card?.icon} size={16} color="white" />
                  </div>
                  <p className="text-xs text-muted-foreground leading-tight">{card?.label}</p>
                  <p className="text-sm font-bold text-foreground mt-0.5">{card?.isNumber ? card?.value : card?.value}</p>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-3 p-4 bg-error/10 border border-error/30 rounded-xl text-error text-sm no-print">
              <Icon name="AlertCircle" size={16} />{error}
            </div>
          )}

          {/* Screen Table */}
          <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden no-print">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold text-foreground">Payment Details</h2>
              <span className="text-sm text-muted-foreground">{reportData?.rows?.length || 0} records</span>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (reportData?.rows?.length || 0) === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Icon name="FileText" size={36} className="text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No payment records found for the selected filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-3 py-3 font-semibold text-muted-foreground text-xs">#</th>
                      <th className="text-left px-3 py-3 font-semibold text-muted-foreground text-xs">Ticket #</th>
                      <th className="text-left px-3 py-3 font-semibold text-muted-foreground text-xs">Project</th>
                      <th className="text-left px-3 py-3 font-semibold text-muted-foreground text-xs">Client</th>
                      <th className="text-left px-3 py-3 font-semibold text-muted-foreground text-xs">Employee</th>
                      <th className="text-left px-3 py-3 font-semibold text-muted-foreground text-xs">Department</th>
                      <th className="text-left px-3 py-3 font-semibold text-muted-foreground text-xs">Role</th>
                      <th className="text-left px-3 py-3 font-semibold text-muted-foreground text-xs">Type</th>
                      <th className="text-right px-3 py-3 font-semibold text-muted-foreground text-xs">Incentive</th>
                      <th className="text-left px-3 py-3 font-semibold text-muted-foreground text-xs">Payment Status</th>
                      <th className="text-left px-3 py-3 font-semibold text-muted-foreground text-xs">Reference</th>
                      <th className="text-left px-3 py-3 font-semibold text-muted-foreground text-xs">Pay Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {reportData?.rows?.map((row, idx) => (
                      <tr key={idx} className="hover:bg-muted/10 transition-colors">
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">{idx + 1}</td>
                        <td className="px-3 py-2.5 font-mono font-semibold text-primary text-xs">{row?.ticketNumber}</td>
                        <td className="px-3 py-2.5 font-medium text-foreground text-xs max-w-[120px] truncate">{row?.projectName}</td>
                        <td className="px-3 py-2.5 text-muted-foreground text-xs">{row?.clientName || '—'}</td>
                        <td className="px-3 py-2.5 font-medium text-foreground text-xs">{row?.employeeName}</td>
                        <td className="px-3 py-2.5 text-muted-foreground text-xs">{row?.department}</td>
                        <td className="px-3 py-2.5 text-muted-foreground text-xs">{row?.role}</td>
                        <td className="px-3 py-2.5 text-xs">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${row?.memberType === 'Sales' ? 'bg-blue-500/15 text-blue-400' : 'bg-purple-500/15 text-purple-400'}`}>
                            {row?.memberType}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-bold text-primary text-xs">{formatOMR(row?.incentiveAmount)}</td>
                        <td className="px-3 py-2.5 text-xs">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${row?.paymentStatus === 'Paid' ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'}`}>
                            {row?.paymentStatus}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{row?.paymentReference}</td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">{row?.paymentDate && row?.paymentDate !== '—' ? new Date(row?.paymentDate)?.toLocaleDateString('en-GB') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/20 border-t-2 border-border">
                      <td colSpan={8} className="px-3 py-3 font-bold text-foreground text-sm">Grand Total</td>
                      <td className="px-3 py-3 text-right font-bold text-primary text-base">{formatOMR(reportData?.summary?.grandTotal || 0)}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </MainLayout>
      {/* ===== PRINT AREA ===== */}
      <div id="finance-print-area" style={{ display: 'none' }}>
        <div style={{ fontFamily: 'Arial, sans-serif', color: '#111', background: 'white', padding: '0' }}>
          {/* Company Header */}
          <div style={{ borderBottom: '3px solid #1a3a5c', paddingBottom: '12px', marginBottom: '16px' }}>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ fontSize: '18pt', fontWeight: 'bold', color: '#1a3a5c' }}>{COMPANY_NAME}</div>
                    <div style={{ fontSize: '10pt', color: '#6b7280', marginTop: '2px' }}>{COMPANY_SUBTITLE}</div>
                  </td>
                  <td style={{ textAlign: 'right', verticalAlign: 'top' }}>
                    <div style={{ fontSize: '14pt', fontWeight: 'bold', color: '#1a3a5c' }}>FINANCE PAYMENT REPORT</div>
                    <div style={{ fontSize: '9pt', color: '#6b7280', marginTop: '4px' }}>Report No: {reportMeta?.reportNo}</div>
                    {reportMeta?.batchNo && <div style={{ fontSize: '9pt', color: '#6b7280' }}>Batch No: {reportMeta?.batchNo}</div>}
                    <div style={{ fontSize: '9pt', color: '#6b7280' }}>Generated: {generatedDate}</div>
                    <div style={{ fontSize: '9pt', color: '#6b7280' }}>Generated By: {generatedBy}</div>
                    <div style={{ fontSize: '9pt', color: '#6b7280' }}>Print Date: {new Date()?.toLocaleDateString('en-GB')}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Report Meta */}
          <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {filters?.dateFrom && <span style={{ fontSize: '9pt' }}><strong>Period From:</strong> {filters?.dateFrom}</span>}
            {filters?.dateTo && <span style={{ fontSize: '9pt' }}><strong>Period To:</strong> {filters?.dateTo}</span>}
            {reportMeta?.preparedBy && <span style={{ fontSize: '9pt' }}><strong>Prepared By:</strong> {reportMeta?.preparedBy}</span>}
            {reportMeta?.reviewedBy && <span style={{ fontSize: '9pt' }}><strong>Reviewed By:</strong> {reportMeta?.reviewedBy}</span>}
            {reportMeta?.approvedBy && <span style={{ fontSize: '9pt' }}><strong>Approved By:</strong> {reportMeta?.approvedBy}</span>}
          </div>

          {/* Summary Boxes */}
          <div style={{ marginBottom: '16px', padding: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <div style={{ fontSize: '10pt', fontWeight: 'bold', color: '#1a3a5c', marginBottom: '8px' }}>SUMMARY</div>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  {[
                    { label: 'Total Sales Incentives', value: formatOMR(reportData?.summary?.totalSalesIncentives || 0) },
                    { label: 'Total Project Team Incentives', value: formatOMR(reportData?.summary?.totalProjectIncentives || 0) },
                    { label: 'Grand Total Payable', value: formatOMR(reportData?.summary?.grandTotal || 0) },
                    { label: 'Paid Amount', value: formatOMR(reportData?.summary?.paidAmount || 0) },
                    { label: 'Pending Amount', value: formatOMR(reportData?.summary?.pendingAmount || 0) },
                    { label: 'No. of Employees', value: reportData?.summary?.numberOfEmployees || 0 },
                    { label: 'No. of Tickets', value: reportData?.summary?.numberOfTickets || 0 },
                  ]?.map((s, i) => (
                    <td key={i} style={{ textAlign: 'center', padding: '6px 8px', border: '1px solid #e2e8f0', background: 'white' }}>
                      <div style={{ fontSize: '8pt', color: '#6b7280' }}>{s?.label}</div>
                      <div style={{ fontSize: '11pt', fontWeight: 'bold', color: '#1a3a5c' }}>{s?.value}</div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Data Table */}
          <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
            <thead>
              <tr style={{ background: '#1a3a5c' }}>
                {['No.', 'Ticket #', 'Project Name', 'Client Name', 'Employee Name', 'Department', 'Role', 'Incentive Type', 'Incentive Amount', 'Payment Status', 'Payment Reference', 'Payment Date']?.map(h => (
                  <th key={h} style={{ background: '#1a3a5c', color: 'white', padding: '7px 8px', textAlign: 'left', fontSize: '9pt', fontWeight: 'bold', borderRight: '1px solid #2d5a8e' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(reportData?.rows || [])?.map((row, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{idx + 1}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#1a3a5c' }}>{row?.ticketNumber}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{row?.projectName}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{row?.clientName || '—'}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold' }}>{row?.employeeName}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{row?.department}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{row?.role}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{row?.memberType}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 'bold' }}>{formatOMR(row?.incentiveAmount)}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{row?.paymentStatus}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace' }}>{row?.paymentReference}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{row?.paymentDate && row?.paymentDate !== '—' ? new Date(row?.paymentDate)?.toLocaleDateString('en-GB') : '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#1a3a5c' }}>
                <td colSpan={8} style={{ padding: '7px 8px', fontSize: '10pt', fontWeight: 'bold', color: 'white' }}>GRAND TOTAL</td>
                <td style={{ padding: '7px 8px', fontSize: '10pt', fontWeight: 'bold', color: 'white', textAlign: 'right' }}>{formatOMR(reportData?.summary?.grandTotal || 0)}</td>
                <td colSpan={3} style={{ padding: '7px 8px', color: 'white' }}></td>
              </tr>
            </tfoot>
          </table>

          {/* Notes */}
          {reportMeta?.notes && (
            <div style={{ marginBottom: '20px', padding: '8px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '4px', fontSize: '9pt' }}>
              <strong>Notes:</strong> {reportMeta?.notes}
            </div>
          )}

          {/* Signature Section */}
          <div style={{ marginTop: '40px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  {[
                    { label: 'Prepared By', name: reportMeta?.preparedBy },
                    { label: 'Finance Reviewed By', name: reportMeta?.reviewedBy },
                    { label: 'Approved By', name: reportMeta?.approvedBy },
                  ]?.map((sig, i) => (
                    <td key={i} style={{ width: '33%', paddingRight: '20px', verticalAlign: 'bottom' }}>
                      <div style={{ borderTop: '1px solid #374151', paddingTop: '6px', marginTop: '40px' }}>
                        <div style={{ fontSize: '9pt', fontWeight: 'bold', color: '#1a3a5c' }}>{sig?.label}</div>
                        {sig?.name && <div style={{ fontSize: '9pt', color: '#374151' }}>{sig?.name}</div>}
                        <div style={{ fontSize: '8pt', color: '#9ca3af', marginTop: '2px' }}>Signature & Date</div>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '8pt', color: '#9ca3af' }}>
            <span>{COMPANY_NAME} — Confidential</span>
            <span>Generated: {generatedDate} by {generatedBy}</span>
            <span>Page 1</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default FinancePaymentReport;
